// Personal reports and export (architecture sprint-04 §5.1, §6.3). Keys sit under ['entries'] so the
// existing entry invalidation refreshes them.
import { useMutation, useQuery, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import type { LocalDate } from '@shared/calendar';
import { apiRequest, downloadFile } from '@/lib/api';
import { LIVE_QUERY_OPTIONS } from '@/lib/live-query';
import type { Project } from '@/types/projects';
import type { DailyTotals, ReportProjectParam } from '@/types/reports';

export type ExportFormat = 'csv' | 'xlsx';

/** The query string for a range and a project filter ('all' omits projectId). */
export function rangeQuery(from: LocalDate, to: LocalDate, project: ReportProjectParam | undefined): string {
  const params = new URLSearchParams({ from, to });
  if (project && project !== 'all') params.set('projectId', project);
  return params.toString();
}

export function useDailyTotals(
  from: LocalDate,
  to: LocalDate,
  project: ReportProjectParam | undefined,
  enabled = true,
): UseQueryResult<DailyTotals> {
  const key = project ?? 'all';
  return useQuery({
    queryKey: ['entries', 'daily-totals', from, to, key],
    queryFn: () => apiRequest<DailyTotals>(`/api/reports/daily-totals?${rangeQuery(from, to, project)}`),
    enabled,
    ...LIVE_QUERY_OPTIONS,
  });
}

export function useReportProjects(): UseQueryResult<Project[]> {
  return useQuery({
    queryKey: ['entries', 'report-projects'],
    queryFn: async () => (await apiRequest<{ items: Project[] }>('/api/reports/projects')).items,
  });
}

export interface ExportRequest {
  from: LocalDate;
  to: LocalDate;
  format: ExportFormat;
  project: ReportProjectParam;
}

export function exportPath({ from, to, format, project }: ExportRequest): string {
  const params = new URLSearchParams({ from, to, format });
  if (project !== 'all') params.set('projectId', project);
  return `/api/exports/entries?${params.toString()}`;
}

/** Not silent: EXPORT_TOO_LARGE and friends use the global toast with the server message. */
export function useExport(): UseMutationResult<void, Error, ExportRequest> {
  return useMutation({
    mutationFn: (req) => downloadFile(exportPath(req), `clockit-timesheet_${req.from}_${req.to}.${req.format}`),
  });
}
